import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatCents, MANUAL_PAYMENT_METHODS, PAYMENT_METHOD_LABELS, type InvoicePayment, type InvoiceSettlement, type PaymentMethod } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { CollapsibleCard } from '../../components/CollapsibleCard';
import { InfoHint } from '../../components/InfoHint';
import { centsText, parseCents } from '../../money';
import { useSession } from '../../session';
import { invoiceDay } from './InvoicesPage';
import './invoices.css';

const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });

/**
 * Zahlungen zu einer ausgestellten Rechnung (KON-152): Liste mit Teilzahlungen, Skonto und Rücknahmen, dazu das Formular „Zahlung erfassen“. Zahlungen werden nie
 * geändert oder gelöscht, ein Irrtum wird mit einer Gegenzahlung zurückgenommen. Der Stand (offen, teilweise, bezahlt) kommt vom Server.
 * Dieselbe Karte dient den Eingangsrechnungen (KON-142, `direction="in"`): Dort gehen die Zahlungen hinaus.
 */
export interface PayableInvoice {
  id: string;
  settlement: InvoiceSettlement | null;
}

const scopes = {
  out: { base: '/invoices-out', key: 'invoice-out', list: 'invoices-out', permission: 'invoices_out.payments' as const },
  in: { base: '/invoices-in', key: 'purchase-invoice', list: 'purchase-invoices', permission: 'invoices_in.payments' as const },
};

export function InvoicePayments({ invoice: i, direction = 'out' }: { invoice: PayableInvoice; direction?: 'out' | 'in' }) {
  const scope = scopes[direction];
  const { can } = useSession();
  const client = useQueryClient();
  const s = i.settlement;
  const [adding, setAdding] = useState(false);
  const [reversing, setReversing] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({ queryKey: [scope.key, i.id, 'payments'], queryFn: async () => (await api<{ data: InvoicePayment[] }>('GET', `${scope.base}/${i.id}/payments`)).data, enabled: !!s });

  const refresh = (data: InvoicePayment[]) => {
    client.setQueryData([scope.key, i.id, 'payments'], data);
    client.invalidateQueries({ queryKey: [scope.key, i.id] });
    client.invalidateQueries({ queryKey: [scope.list] });
    client.invalidateQueries({ queryKey: ['open-balance'] });
    client.invalidateQueries({ queryKey: ['contact-stats'] });
  };
  const reverse = useMutation({
    mutationFn: async (paymentId: string) => (await api<{ data: InvoicePayment[] }>('POST', `${scope.base}/${i.id}/payments/${paymentId}/reverse`, { reason: reason.trim() })).data,
    onSuccess: (data) => {
      refresh(data);
      setReversing(null);
      setReason('');
      setError(null);
    },
    onError: (e) => setError(e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Die Zahlung konnte nicht zurückgenommen werden.'),
  });

  if (!s) return null;
  const mayPay = can(scope.permission);
  const payments = list.data ?? [];

  return (
    <CollapsibleCard
      title="Zahlungen"
      storageKey={`${scope.key}-payments`}
      defaultOpen
      actions={mayPay && s.openCents > 0 && !adding ? <button type="button" className="btn btn--primary btn--sm" onClick={() => setAdding(true)}>Zahlung erfassen</button> : null}
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {adding && <PaymentForm invoice={i} base={scope.base} onDone={(data) => { if (data) refresh(data); setAdding(false); }} />}
      {list.isLoading && <p className="muted">Wird geladen …</p>}
      {list.data && payments.length === 0 && !adding && <p className="muted">Noch keine Zahlung erfasst. Offen: {formatCents(s.openCents)}.</p>}
      {payments.length > 0 && (
        <table className="table table--cards">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Art</th>
              <th className="num">Betrag</th>
              <th className="num">Skonto</th>
              <th>Hinweis</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.id} className={p.reversed || p.reversesPaymentId ? 'row--muted' : undefined}>
                <td data-label="Datum">{invoiceDay(p.paidOn)}</td>
                <td data-label="Art">{p.reversesPaymentId ? 'Rücknahme' : PAYMENT_METHOD_LABELS[p.method]}</td>
                <td data-label="Betrag" className="num">{formatCents(p.amountCents)}</td>
                <td data-label="Skonto" className="num">{p.discountCents ? formatCents(p.discountCents) : '–'}</td>
                <td data-label="Hinweis">
                  {[p.reference, p.note, p.reversed ? 'zurückgenommen' : null, p.createdByName].filter(Boolean).join(' · ') || '–'}
                </td>
                <td>
                  {mayPay && !p.reversed && !p.reversesPaymentId && p.method !== 'offset' && (
                    reversing === p.id ? (
                      <form
                        className="payment-reverse"
                        onSubmit={(e) => {
                          e.preventDefault();
                          if (!reason.trim()) return setError('Bitte geben Sie einen Grund an.');
                          reverse.mutate(p.id);
                        }}
                      >
                        <input className="input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Grund, zum Beispiel Rücklastschrift" aria-label="Grund der Rücknahme" maxLength={1000} />
                        <button type="submit" className="btn btn--danger btn--sm" disabled={reverse.isPending}>Zurücknehmen</button>
                        <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setReversing(null); setReason(''); }}>Abbrechen</button>
                      </form>
                    ) : (
                      <button type="button" className="btn btn--secondary btn--sm" onClick={() => { setReversing(p.id); setError(null); }}>Zurücknehmen …</button>
                    )
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className="muted small">Zahlungen bleiben unverändert stehen. Einen Irrtum nehmen Sie mit einer Gegenzahlung zurück, beides bleibt nachvollziehbar.</p>
    </CollapsibleCard>
  );
}

function PaymentForm({ invoice: i, base, onDone }: { invoice: PayableInvoice; base: string; onDone: (data: InvoicePayment[] | null) => void }) {
  const s = i.settlement!;
  const [amount, setAmount] = useState(centsText(s.openCents));
  const [date, setDate] = useState(today());
  const [method, setMethod] = useState<PaymentMethod>('bank_transfer');
  const [discount, setDiscount] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const save = useMutation({
    mutationFn: async () => {
      const amountCents = parseCents(amount);
      const discountCents = discount.trim() ? parseCents(discount) : 0;
      if (amountCents === null || amountCents <= 0) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { amountCents: 'Bitte einen Betrag größer als 0 angeben.' });
      if (discountCents === null || discountCents < 0) throw new ApiError(422, 'validation_failed', 'Bitte prüfen Sie Ihre Eingaben.', { discountCents: 'Bitte einen gültigen Betrag angeben.' });
      return (await api<{ data: InvoicePayment[] }>('POST', `${base}/${i.id}/payments`, { amountCents, discountCents, paidOn: date, method, reference: reference.trim(), note: note.trim() })).data;
    },
    onSuccess: (data) => onDone(data),
    onError: (e) => {
      if (e instanceof ApiError && e.status === 422) {
        setErrors(e.fields);
        setError('Bitte prüfen Sie die markierten Felder.');
      } else setError(e instanceof ApiError ? e.message : 'Die Zahlung konnte nicht erfasst werden.');
    },
  });
  const err = (key: string) => (errors[key] ? <small className="field__error" role="alert">{errors[key]}</small> : null);

  /** Mit Skonto zahlt der Kunde weniger als den offenen Betrag, der Rest gilt als abgezogenes Skonto */
  function takeDiscount() {
    setDiscount(centsText(s.discountAvailableCents));
    setAmount(centsText(s.openCents - s.discountAvailableCents));
  }

  return (
    <form
      className="payment-form"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        setError(null);
        setErrors({});
        save.mutate();
      }}
      noValidate
    >
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      <div className="form-grid">
        <label className="field">
          <span>Betrag € (Geldeingang)</span>
          <input className="input num" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} aria-invalid={!!errors.amountCents} />
          {err('amountCents')}
        </label>
        <label className="field">
          <span>Datum der Zahlung</span>
          <input className="input" type="date" value={date} max={today()} onChange={(e) => setDate(e.target.value)} aria-invalid={!!errors.paidOn} />
          {err('paidOn')}
        </label>
        <label className="field">
          <span>Zahlungsart</span>
          <select className="input" value={method} onChange={(e) => setMethod(e.target.value as PaymentMethod)}>
            {MANUAL_PAYMENT_METHODS.map((m) => <option key={m} value={m}>{PAYMENT_METHOD_LABELS[m]}</option>)}
          </select>
          {method === 'cash' && <small className="muted">Barzahlung tragen Sie hier ein. Ein Kassenbuch ersetzt das nicht.</small>}
        </label>
        <label className="field">
          <span className="invoice-label">Skonto € (abgezogen)<InfoHint label="Skonto">Hat der Kunde Skonto abgezogen, tragen Sie den abgezogenen Betrag hier ein. Er mindert den offenen Betrag, ist aber kein Geldeingang. Möglich sind nur die Bedingungen, die auf der Rechnung stehen.</InfoHint></span>
          <input className="input num" inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} placeholder="0,00" disabled={s.discountAvailableCents <= 0} aria-invalid={!!errors.discountCents} />
          {s.discountAvailableCents > 0 && (
            <small className="muted">
              Möglich bis {formatCents(s.discountAvailableCents)}{s.discountUntil ? `, laut Rechnung bei Zahlung bis ${invoiceDay(s.discountUntil)}` : ''}.{' '}
              <button type="button" className="link-button" onClick={takeDiscount}>Skonto abziehen</button>
            </small>
          )}
          {err('discountCents')}
        </label>
        <label className="field">
          <span>Verwendungszweck oder Beleg</span>
          <input className="input" value={reference} onChange={(e) => setReference(e.target.value)} maxLength={200} />
        </label>
        <label className="field">
          <span>Notiz</span>
          <input className="input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={1000} />
        </label>
      </div>
      <div className="form-actions">
        <button className="btn btn--primary" type="submit" disabled={save.isPending}>{save.isPending ? 'Wird gespeichert …' : 'Zahlung speichern'}</button>
        <button type="button" className="btn btn--secondary" onClick={() => onDone(null)}>Abbrechen</button>
      </div>
    </form>
  );
}
